import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import {
    FileText, Sparkles, AlertCircle, CheckCircle2, Download,
    Send, Edit2, FileDown, FolderDown, Copy, BookmarkPlus, LayoutTemplate, Trash2, Globe, Plus, Calculator, ChevronDown, ChevronUp, ShieldCheck, Wallet, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { ModuleForm } from "../components/ModuleForm";
import { FORM_SCHEMAS } from "../modules/formSchemas";

const CALC_MODULES = [];

const STATUS_COLORS = {
    draft: "bg-white text-black border-orange-300",
    review: "bg-primary text-white border-primary",
    ready: "bg-primary text-white border-primary",
    submitted: "bg-primary text-white border-primary",
    accepted: "bg-primary text-white border-primary",
    rejected: "bg-white text-black border-orange-300",
};

const getValue = (obj, path) => path.split(".").reduce((acc, key) => acc?.[key], obj);
const fmtDate = (value) => value ? new Date(value).toLocaleDateString("it-IT") : "—";

function catalogToFormSchema(item) {
    return {
        sections: (item.sections || []).map((section) => {
            if (section.repeatable) {
                const root = (section.fields?.[0]?.path || "righe[]").split("[]")[0];
                return {
                    code: section.code,
                    title: section.title,
                    type: "list",
                    path: root,
                    item_label: "Aggiungi",
                    columns: (section.fields || []).map((field) => ({
                        ...field,
                        key: field.path.split("[].")[1] || field.path,
                        options: (field.options || []).map((o) => typeof o === "string" ? { value: o, label: o } : o),
                    })),
                };
            }
            return {
                ...section,
                type: "single",
                fields: (section.fields || []).map((field) => ({
                    ...field,
                    options: (field.options || []).map((o) => typeof o === "string" ? { value: o, label: o } : o),
                })),
            };
        }),
    };
}

function ComunicaPreflightCard({ data, onRefresh, onTelemaco, onCheck, onPoll, busy }) {
    const local = data.local_package || {};
    const official = data.official_control || {};
    const telemaco = data.telemaco || {};
    const costs = data.costs || {};
    const attachments = data.attachments || {};
    const modules = data.modules || {};
    const allBlockers = official.blockers || [];
    const lastCheck = official.last_check || null;
    return (
        <section data-testid="comunica-preflight" className="mb-6 overflow-hidden rounded-lg border border-orange-300 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-orange-50 px-4 py-3 border-b border-orange-200">
                <div>
                    <div className="flex items-center gap-2 text-sm font-bold uppercase text-orange-950"><ShieldCheck size={16} /> Pre-invio ComUnica</div>
                    <div className="mt-1 text-xs text-orange-900/80">Fedra target {data.fedra_target} · verifica locale distinta dal controllo ufficiale InfoCamere</div>
                </div>
                <button onClick={onRefresh} className="rounded-md border border-orange-300 bg-white px-3 py-1.5 text-xs font-bold uppercase text-orange-800 hover:bg-orange-50">Aggiorna</button>
            </div>
            <div className="grid gap-3 p-4 lg:grid-cols-2">
                <PreflightStatus
                    title="Pacchetto locale"
                    ok={Boolean(local.ready)}
                    text={local.ready ? "Dati validi per generare il pacchetto Lab" : `${(local.blockers || []).length} blocchi da risolvere`}
                    detail={local.generated ? `Generato${local.sha256 ? ` · SHA-256 ${local.sha256.slice(0, 12)}…` : ""}` : "Non ancora generato"}
                />
                <div className="rounded-md border border-orange-200 p-3 text-xs lg:col-span-2">
                    <div className="mb-2 font-bold uppercase text-orange-900">Collaudo adapter</div>
                    <div>{lastCheck ? `Ultimo controllo: ${lastCheck.status || "—"} · ${lastCheck.mode || "mock"} · ${lastCheck.external_practice_id || ""}` : "Nessun controllo ancora eseguito"}</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button disabled={busy || !local.ready} onClick={onCheck} className="rounded bg-primary px-3 py-2 font-bold uppercase text-white disabled:opacity-40">Esegui controllo mock</button>
                        <button disabled={busy || !lastCheck} onClick={onPoll} className="rounded border border-orange-300 px-3 py-2 font-bold uppercase text-orange-800 disabled:opacity-40">Aggiorna esito</button>
                    </div>
                </div>
                <PreflightStatus
                    title="Controllo InfoCamere"
                    ok={Boolean(official.ready)}
                    text={official.ready ? "Pronto per il collaudo" : "Non ancora abilitato"}
                    detail={official.message}
                />
                <div className="rounded-md border border-orange-200 p-3 text-xs">
                    <div className="mb-2 font-bold uppercase text-orange-900">Moduli e allegati</div>
                    <div>Attesi: {(modules.expected || []).join(", ") || "profilo non disponibile"}</div>
                    <div className="mt-1">File modulo generati: {(modules.generated_files || []).length}</div>
                    <div className="mt-1">Allegati caricati: {attachments.count || 0}</div>
                    {(attachments.required_documents || []).length > 0 && <div className="mt-2 text-orange-800">Documenti da verificare: {(attachments.required_documents || []).join(", ")}</div>}
                </div>
                <div className="rounded-md border border-orange-200 p-3 text-xs">
                    <div className="mb-2 flex items-center gap-2 font-bold uppercase text-orange-900"><Link2 size={14} /> Utente Telemaco</div>
                    <div>{telemaco.connected ? `Collegato${telemaco.subject ? `: ${telemaco.subject}` : ""}` : "Non collegato"}</div>
                    <div className="mt-1 text-black/60">Saldo diritti/tariffe non esposto dalle API documentate.</div>
                    <button onClick={onTelemaco} className="mt-2 rounded border border-orange-300 px-2 py-1 font-bold uppercase text-orange-800 hover:bg-orange-50">Gestisci Telemaco</button>
                </div>
                <div className="rounded-md border border-orange-200 p-3 text-xs lg:col-span-2">
                    <div className="mb-2 flex items-center gap-2 font-bold uppercase text-orange-900"><Wallet size={14} /> Costi separati</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                        <div><strong>Crediti FiscoX:</strong> {costs.software_credits?.status === "NOT_CONFIGURED" ? "tariffa ComUnica da configurare; nessun addebito ora" : costs.software_credits?.amount}</div>
                        <div><strong>Diritti, bolli e tariffe:</strong> stima ufficiale non disponibile; saranno addebitati al conto Telemaco dell'affiliato.</div>
                    </div>
                </div>
            </div>
            {allBlockers.length > 0 && (
                <div className="border-t border-orange-200 bg-orange-50 px-4 py-3">
                    <div className="mb-2 text-xs font-bold uppercase text-orange-900">Blocchi prima del controllo ufficiale</div>
                    <ul className="space-y-1 text-xs text-orange-900">
                        {allBlockers.map((item, index) => <li key={`${item.code}-${index}`}>• {item.message}</li>)}
                    </ul>
                </div>
            )}
            <div className="border-t border-orange-200 px-4 py-3 text-[11px] font-semibold text-orange-800">
                Nessuna trasmissione esterna è stata eseguita. Il pulsante di invio resta disabilitato fino al collaudo InfoCamere.
            </div>
        </section>
    );
}

function PreflightStatus({ title, ok, text, detail }) {
    return (
        <div className={`rounded-md border p-3 ${ok ? "border-green-300 bg-green-50" : "border-orange-300 bg-orange-50"}`}>
            <div className={`flex items-center gap-2 text-xs font-bold uppercase ${ok ? "text-green-800" : "text-orange-900"}`}>
                {ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {title}
            </div>
            <div className="mt-2 text-sm font-semibold text-black">{text}</div>
            {detail && <div className="mt-1 text-[11px] text-black/60">{detail}</div>}
        </div>
    );
}

function praticaSubject(p) {
    const f = p.form_data || {};
    const qb = f.quadro_b || {};
    if (p.module_code === "AA7_10") return qb.denominazione || p.title || "—";
    return [qb.cognome, qb.nome].filter(Boolean).join(" ") || p.title || "—";
}

function tipoDocumento(p) {
    const tipo = getValue(p.form_data || {}, "tipo_dichiarazione");
    const modello = p.module_code === "AA7_10" ? "AA7/10 — società/ente" : "AA9/12 — persona fisica";
    const azione = { "1": "Apertura P.IVA", "2": "Variazione P.IVA", "3": "Cessazione P.IVA" }[tipo] || "Pratica P.IVA";
    return `${modello} — ${azione}`;
}

function formaGiuridica(p) {
    if (p.module_code === "AA9_12") return "Ditta individuale";
    const natura = getValue(p.form_data || {}, "quadro_b.natura_giuridica") || "—";
    return String(natura).replace(/_/g, " ").toUpperCase();
}

function yesNoBadge(ok, positive = true) {
    if (!ok) return <span className="text-xs text-muted-foreground">—</span>;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase border ${positive ? "bg-orange-50 text-orange-800 border-orange-200" : "bg-orange-50 text-orange-800 border-orange-200"}`}>
        {positive ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />} Sì
    </span>;
}

export default function PraticheList() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialTab = ["to_send", "sent", "templates"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "to_send";
    const [tab, setTabState] = useState(initialTab);
    useEffect(() => {
        const next = ["to_send", "sent", "templates"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "to_send";
        setTabState(next);
    }, [searchParams]);
    const [list, setList] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [invii, setInvii] = useState([]);

    const load = useCallback(async () => {
        try {
            const [r, rt, ri] = await Promise.all([api.get("/pratiche"), api.get("/templates"), api.get("/telematico/invii")]);
            setList(r.data);
            setTemplates(rt.data);
            setInvii(ri.data || []);
        } catch {}
    }, []);

    useEffect(() => { load(); }, [load]);

    const applyTemplate = async (tpl) => {
        try {
            const r = await api.post(`/templates/${tpl.id}/use`, {});
            toast.success(t.templates.createdFromTemplate);
            navigate(`/pratiche/${r.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const deleteTemplate = async (tpl) => {
        if (!window.confirm(t.templates.confirmDelete)) return;
        try {
            await api.delete(`/templates/${tpl.id}`);
            toast.success(t.templates.deleted);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        }
    };

    const downloadRicevuta = async (invioId) => {
        const tid = toast.loading("Download ricevuta...");
        try {
            const r = await api.post(`/telematico/invii/${invioId}/ricevuta-token?fmt=pdf`);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const a = document.createElement("a");
            a.href = `${backendUrl}${r.data.url}`;
            a.download = r.data.filename;
            a.rel = "noopener";
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => a.remove(), 1500);
            toast.success(t.telematico.ricevutaDownloaded, { id: tid });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore download", { id: tid });
        }
    };

    const invioRank = (inv) => {
        // Per la vista "Inviate" deve prevalere l'invio reale consultabile:
        // prima quelli con ricevuta, poi quelli protocollati, solo dopo i tentativi falliti/tecnici.
        if (inv?.ricevuta_rel_path || inv?.ricevuta_path || inv?.ricevuta_status === "available") return 3;
        if (inv?.protocol) return 2;
        if (inv?.status === "completed") return 1;
        return 0;
    };

    const latestInvioByPratica = invii.reduce((acc, inv) => {
        const current = acc[inv.pratica_id];
        if (
            !current ||
            invioRank(inv) > invioRank(current) ||
            (invioRank(inv) === invioRank(current) && new Date(inv.created_at || 0) > new Date(current.created_at || 0))
        ) acc[inv.pratica_id] = inv;
        return acc;
    }, {});
    const rows = list.map((p) => ({ pratica: p, invio: latestInvioByPratica[p.id] }));
    const sentRows = rows.filter(({ pratica, invio }) => invio || ["submitted", "accepted", "rejected"].includes(pratica.status));
    const toSendRows = rows.filter(({ pratica, invio }) => !invio && !["submitted", "accepted", "rejected"].includes(pratica.status));
    const sectionTitle = `${t.nav.pivaAde} - ${tab === "sent" ? t.nav.sent : tab === "templates" ? t.nav.templates : t.nav.toSend}`;
    const sectionSubtitle = tab === "sent"
        ? "Pratiche P. IVA già trasmesse all'Agenzia delle Entrate."
        : tab === "templates"
            ? "Modelli P. IVA AdE salvati e riutilizzabili."
            : "Pratiche P. IVA pronte da lavorare e inviare all'Agenzia delle Entrate.";

    return (
        <div className="min-h-screen p-6 lg:p-10">
            <section data-testid="module-piva-ade" className="rounded-xl border-2 border-orange-300 bg-white shadow-sm overflow-hidden">
                <div className="flex items-start justify-between gap-4 flex-wrap bg-primary px-5 py-4 text-white">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase tracking-tight">{sectionTitle}</h1>
                        <p className="mt-1 text-sm font-bold uppercase text-white/90">{sectionSubtitle}</p>
                    </div>
                    <Link
                        data-testid="new-pratica-from-chat"
                        to="/chat"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-white bg-white text-orange-700 hover:bg-orange-50 text-sm font-bold uppercase transition-colors"
                    >
                        <Sparkles size={14} /> {t.pratiche.newPratica}
                    </Link>
                </div>

                <div className="p-5">
                    {tab === "to_send" ? (
                        <PraticheTable rows={toSendRows} navigate={navigate} downloadRicevuta={downloadRicevuta} emptyTitle={t.pratiche.empty} emptyHint={t.pratiche.emptyHint} mode="to_send" reload={load} />
                    ) : tab === "sent" ? (
                        <PraticheTable rows={sentRows} navigate={navigate} downloadRicevuta={downloadRicevuta} emptyTitle="Nessuna pratica inviata" emptyHint="Quando una pratica viene trasmessa all'Agenzia delle Entrate apparirà qui." mode="sent" reload={load} />
                    ) : (
                        templates.length === 0 ? (
                            <div data-testid="templates-empty" className="rounded-lg border border-orange-300 bg-white p-16 text-center">
                                <LayoutTemplate size={32} className="mx-auto text-primary mb-4" />
                                <h3 className="font-display text-lg font-bold uppercase mb-1">{t.templates.empty}</h3>
                                <p className="text-sm text-black">{t.templates.emptyHint}</p>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-orange-300 bg-white divide-y divide-orange-200">
                                {templates.map((tpl) => (
                                    <div key={tpl.id} data-testid={`template-row-${tpl.id}`} className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 rounded-md bg-orange-50 text-primary flex items-center justify-center flex-shrink-0">
                                                <LayoutTemplate size={16} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-bold uppercase text-sm truncate flex items-center gap-2">
                                                    {tpl.name}
                                                    {tpl.is_global && (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-50 text-orange-800 border border-orange-200">
                                                            <Globe size={9} /> {t.templates.global}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-black truncate">{tpl.module_code}{tpl.description ? ` · ${tpl.description}` : ""}</div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button data-testid={`use-template-${tpl.id}`} onClick={() => applyTemplate(tpl)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold uppercase transition-colors">
                                                <Plus size={12} /> {t.templates.use}
                                            </button>
                                            <button data-testid={`delete-template-${tpl.id}`} onClick={() => deleteTemplate(tpl)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-orange-300 bg-white hover:bg-orange-50 text-xs transition-colors text-black">
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            </section>
        </div>
    );
}

function PraticheTable({ rows, navigate, downloadRicevuta, emptyTitle, emptyHint, mode = "sent", reload }) {
    const { t } = useI18n();
    const [filters, setFilters] = useState({ name: "", tipo: "", forma: "", esito: "" });
    const [selected, setSelected] = useState({});
    const [busyBatch, setBusyBatch] = useState(false);
    const [draggedColumn, setDraggedColumn] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: "", dir: "asc" });
    const defaultColumnKeysForMode = (m) => m === "to_send"
        ? ["subject", "tipo", "forma", "created"]
        : ["subject", "tipo", "forma", "dataInvio", "invio", "acquisito", "scartato", "ricevuta"];
    const normalizeColumnOrder = (saved, defaults) => {
        const arr = Array.isArray(saved) ? saved : [];
        return [...arr.filter((k) => defaults.includes(k)), ...defaults.filter((k) => !arr.includes(k))];
    };
    const columnStorageKey = `fiscoai_pratiche_columns_${mode}`;
    const widthStorageKey = `fiscoai_pratiche_column_widths_${mode}`;
    const [columnOrder, setColumnOrder] = useState(() => {
        try { return normalizeColumnOrder(JSON.parse(localStorage.getItem(columnStorageKey)), defaultColumnKeysForMode(mode)); }
        catch { return defaultColumnKeysForMode(mode); }
    });
    const [columnWidths, setColumnWidths] = useState(() => {
        try { return JSON.parse(localStorage.getItem(widthStorageKey)) || {}; }
        catch { return {}; }
    });

    useEffect(() => {
        try { setColumnOrder(normalizeColumnOrder(JSON.parse(localStorage.getItem(columnStorageKey)), defaultColumnKeysForMode(mode))); }
        catch { setColumnOrder(defaultColumnKeysForMode(mode)); }
        try { setColumnWidths(JSON.parse(localStorage.getItem(widthStorageKey)) || {}); }
        catch { setColumnWidths({}); }
    }, [mode, columnStorageKey, widthStorageKey]);

    const moveColumn = (fromKey, toKey) => {
        if (!fromKey || !toKey || fromKey === toKey) return;
        setColumnOrder((cur) => {
            const next = [...cur];
            const from = next.indexOf(fromKey);
            const to = next.indexOf(toKey);
            if (from < 0 || to < 0) return cur;
            const [item] = next.splice(from, 1);
            next.splice(to, 0, item);
            localStorage.setItem(columnStorageKey, JSON.stringify(next));
            return next;
        });
    };

    const resizeColumn = (key, e) => {
        e.preventDefault();
        e.stopPropagation();
        const th = e.currentTarget.closest("th");
        const startX = e.clientX;
        const startWidth = th?.getBoundingClientRect().width || columnWidths[key] || 140;
        const onMove = (ev) => {
            const width = Math.max(70, Math.round(startWidth + ev.clientX - startX));
            setColumnWidths((cur) => {
                const next = { ...cur, [key]: width };
                localStorage.setItem(widthStorageKey, JSON.stringify(next));
                return next;
            });
        };
        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            document.body.classList.remove("select-none", "cursor-col-resize");
        };
        document.body.classList.add("select-none", "cursor-col-resize");
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const tableRows = rows.map(({ pratica: p, invio }) => {
        const scartato = invio?.esito === "SCARTATA" || p.status === "rejected";
        const accolto = invio?.esito === "ACCOLTA" || p.status === "accepted";
        // Invio riuscito/protocollo = solo trasmissione avvenuta.
        // Accettato/Scartato si valorizzano solo dopo ricevuta ufficiale interpretata.
        const acquisito = !scartato && accolto;
        return {
            p,
            invio,
            subject: praticaSubject(p),
            tipo: tipoDocumento(p),
            forma: formaGiuridica(p),
            dataInvio: fmtDate(invio?.created_at),
            protocollo: invio?.protocol || p.submitted_protocol || "—",
            acquisito: acquisito ? 1 : "",
            scartato: scartato ? 1 : "",
            esito: scartato ? "SCARTATA" : (accolto ? "ACCOLTA" : ""),
            invioStatus: invio?.status === "failed" && !invio?.protocol ? "FALLITO" : (invio?.protocol || invio?.status === "completed" || invio?.status === "sent" || p.submitted_protocol ? "RIUSCITO" : ""),
            hasReceipt: Boolean(invio && (invio.ricevuta_rel_path || invio.ricevuta_path)),
        };
    });

    const unique = (key) => [...new Set(tableRows.map((r) => r[key]).filter(Boolean))].sort();
    let filteredRows = tableRows.filter((r) => {
        const q = filters.name.trim().toLowerCase();
        if (q && !r.subject.toLowerCase().includes(q)) return false;
        if (filters.tipo && r.tipo !== filters.tipo) return false;
        if (filters.forma && r.forma !== filters.forma) return false;
        if (filters.esito && r.esito !== filters.esito) return false;
        return true;
    });

    const columns = {
        fileName: { label: "Nome file", headerClass: "px-3 py-3 text-left font-bold uppercase min-w-[180px]", cellClass: "px-3 py-3 min-w-[180px] font-bold uppercase text-black", exportValue: (r) => r.p.title || "—", render: (r) => r.p.title || "—" },
        subject: {
            label: "Ragione sociale/Cognome",
            headerClass: "px-3 py-3 text-left font-bold uppercase min-w-[170px]",
            cellClass: "px-3 py-3 min-w-[170px]",
            exportValue: (r) => r.subject,
            render: (r) => <div className="font-bold uppercase text-black">{r.subject}</div>,
        },
        tipo: { label: "Tipo documento", headerClass: "px-2 py-3 text-left font-bold uppercase w-[86px]", cellClass: "px-2 py-3 w-[86px]", exportValue: (r) => r.tipo, render: (r) => r.tipo },
        forma: { label: "Forma giuridica", headerClass: "px-3 py-3 text-left font-bold uppercase min-w-[90px]", cellClass: "px-2 py-3 min-w-[90px]", exportValue: (r) => r.forma, render: (r) => r.forma },
        created: { label: "Data creazione", headerClass: "px-2 py-3 text-left font-bold uppercase", cellClass: "px-2 py-3 whitespace-nowrap", exportValue: (r) => fmtDate(r.p.created_at), sortValue: (r) => new Date(r.p.created_at || 0).getTime(), render: (r) => fmtDate(r.p.created_at) },
        dataInvio: { label: "Data invio", headerClass: "px-2 py-3 text-left font-bold uppercase", cellClass: "px-2 py-3 whitespace-nowrap", exportValue: (r) => r.dataInvio, sortValue: (r) => new Date(r.invio?.created_at || 0).getTime(), render: (r) => r.dataInvio },
        invio: { label: "Invio", headerClass: "px-2 py-3 text-center font-bold uppercase", cellClass: "px-2 py-3 text-center", exportValue: (r) => r.invioStatus, render: (r) => r.invioStatus ? <span className="inline-flex rounded border border-orange-300 bg-white px-2 py-0.5 text-[11px] font-bold uppercase text-orange-800">{r.invioStatus}</span> : "—" },
        acquisito: { label: "Accettato", headerClass: "px-2 py-3 text-center font-bold uppercase", cellClass: "px-2 py-3 text-center font-bold uppercase", exportValue: (r) => r.acquisito, render: (r) => r.acquisito },
        scartato: { label: "Scartato", headerClass: "px-2 py-3 text-center font-bold uppercase", cellClass: "px-2 py-3 text-center font-bold uppercase text-orange-700", exportValue: (r) => r.scartato, render: (r) => r.scartato },
        ricevuta: {
            label: "Ric.",
            headerClass: "px-2 py-3 text-center font-bold uppercase min-w-[72px]",
            cellClass: "px-2 py-3 text-center min-w-[72px]",
            exportValue: (r) => r.hasReceipt ? "Sì" : "",
            render: (r) => r.hasReceipt ? (
                <button
                    data-testid={`pratica-download-ricevuta-${r.p.id}`}
                    onClick={(e) => { e.stopPropagation(); downloadRicevuta(r.invio.id); }}
                    title="Scarica ricevuta PDF"
                    aria-label="Scarica ricevuta PDF"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 hover:text-orange-900 transition-colors"
                >
                    <FolderDown size={18} />
                </button>
            ) : <span className="text-xs text-muted-foreground">—</span>,
        },
    };
    const visibleColumns = columnOrder.map((key) => columns[key]).filter(Boolean);
    const toggleSort = (key) => {
        setSortConfig((cur) => ({ key, dir: cur.key === key && cur.dir === "asc" ? "desc" : "asc" }));
    };
    if (sortConfig.key && columns[sortConfig.key]) {
        const col = columns[sortConfig.key];
        filteredRows = [...filteredRows].sort((a, b) => {
            const rawA = col.sortValue ? col.sortValue(a) : col.exportValue(a);
            const rawB = col.sortValue ? col.sortValue(b) : col.exportValue(b);
            let cmp;
            if (typeof rawA === "number" && typeof rawB === "number") {
                cmp = rawA - rawB;
            } else {
                const av = String(rawA ?? "").toLowerCase();
                const bv = String(rawB ?? "").toLowerCase();
                cmp = av.localeCompare(bv, "it", { numeric: true, sensitivity: "base" });
            }
            return sortConfig.dir === "asc" ? cmp : -cmp;
        });
    }

    const exportRows = () => filteredRows.map((r) => {
        const row = {};
        visibleColumns.forEach((col) => { row[col.label] = col.exportValue(r); });
        return row;
    });

    const downloadBlob = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const exportExcel = () => {
        const data = exportRows();
        const headers = Object.keys(data[0] || Object.fromEntries(visibleColumns.map((col) => [col.label, ""])));
        const html = `<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${data.map((row) => `<tr>${headers.map((h) => `<td>${String(row[h] ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
        downloadBlob(`\ufeff${html}`, "application/vnd.ms-excel", "pratiche.xls");
    };

    const exportPdf = () => {
        const data = exportRows();
        const headers = Object.keys(data[0] || Object.fromEntries(visibleColumns.map((col) => [col.label, ""])));
        const html = `<!doctype html><html><head><title>Pratiche</title><style>body{font-family:Arial,sans-serif;padding:24px}table{width:100%;border-collapse:collapse;font-size:11px}th,td{border:1px solid #ccc;padding:6px;text-align:left}th{background:#eee}@media print{@page{size:landscape}}</style></head><body><h2>Pratiche</h2><table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${data.map((row) => `<tr>${headers.map((h) => `<td>${String(row[h] ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;")}</td>`).join("")}</tr>`).join("")}</tbody></table><script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script></body></html>`;
        const win = window.open("", "_blank");
        if (!win) return toast.error("Popup bloccato: abilita le finestre popup per esportare in PDF.");
        win.document.write(html);
        win.document.close();
    };

    const selectedIds = Object.keys(selected).filter((id) => selected[id]);
    const visibleIds = filteredRows.map((r) => r.p.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected[id]);
    const toggleAllVisible = (checked) => {
        setSelected((cur) => {
            const next = { ...cur };
            visibleIds.forEach((id) => { next[id] = checked; });
            return next;
        });
    };
    const deleteOne = async (id) => {
        if (!window.confirm(t.pratiche.deleteConfirm)) return;
        try {
            await api.delete(`/pratiche/${id}`);
            toast.success("Pratica eliminata");
            await reload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore eliminazione");
        }
    };
    const deleteSelected = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Eliminare definitivamente ${selectedIds.length} pratiche?`)) return;
        setBusyBatch(true);
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`/pratiche/${id}`)));
            setSelected({});
            toast.success("Pratiche eliminate");
            await reload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore eliminazione");
        } finally { setBusyBatch(false); }
    };
    const prepareAndSend = async (pratica) => {
        if (!pratica.file_generated) await api.post(`/pratiche/${pratica.id}/generate`);
        await api.post(`/telematico/invii`, { pratica_id: pratica.id });
    };
    const sendSelected = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Inviare ${selectedIds.length} pratiche all'Agenzia delle Entrate?`)) return;
        setBusyBatch(true);
        let ok = 0;
        let failed = 0;
        const selectedRows = filteredRows.filter((r) => selectedIds.includes(r.p.id));
        for (const row of selectedRows) {
            try {
                await prepareAndSend(row.p);
                ok += 1;
            } catch {
                failed += 1;
            }
        }
        setSelected({});
        await reload?.();
        if (ok) toast.success(`${ok} pratiche avviate all'invio`);
        if (failed) toast.error(`${failed} pratiche non inviate: verifica credito, salvataggio o impostazioni telematiche`);
    };

    if (!rows.length) {
        return (
            <div className="rounded-lg border border-border bg-card p-16 text-center">
                <FileText size={32} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="font-display text-lg font-bold uppercase mb-1">{emptyTitle}</h3>
                <p className="text-sm text-muted-foreground mb-6">{emptyHint}</p>
                <Link to="/chat" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-bold uppercase transition-colors">
                    <Sparkles size={14} /> Nuova pratica
                </Link>
            </div>
        );
    }
    return (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="p-3 border-b border-border flex flex-wrap gap-2 items-center justify-between">
                <div className="flex flex-wrap gap-2">
                    <input value={filters.name} onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))} placeholder="Cerca nominativo/ragione sociale" className="h-9 w-64 rounded-md border border-border bg-background px-3 text-sm" />
                    <select value={filters.tipo} onChange={(e) => setFilters((f) => ({ ...f, tipo: e.target.value }))} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
                        <option value="">Tipo documento: tutti</option>
                        {unique("tipo").map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <select value={filters.forma} onChange={(e) => setFilters((f) => ({ ...f, forma: e.target.value }))} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
                        <option value="">Forma: tutte</option>
                        {unique("forma").map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                    {mode !== "to_send" && (
                        <select value={filters.esito} onChange={(e) => setFilters((f) => ({ ...f, esito: e.target.value }))} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
                            <option value="">Esito: tutti</option>
                            <option value="ACQUISITA">Acquisita</option>
                            <option value="ACCOLTA">Accolta</option>
                            <option value="SCARTATA">Scartata</option>
                        </select>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                    {mode === "to_send" && selectedIds.length > 0 && (
                        <>
                            <span className="inline-flex items-center px-2 text-xs text-muted-foreground">{selectedIds.length} {t.pratiche.selectedCount}</span>
                            <button disabled={busyBatch} onClick={sendSelected} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-white text-xs hover:bg-orange-600 disabled:opacity-50"><Send size={12} /> {t.pratiche.sendSelected}</button>
                            <button disabled={busyBatch} onClick={deleteSelected} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-orange-200 bg-orange-50 text-orange-700 text-xs hover:bg-orange-100 disabled:opacity-50"><Trash2 size={12} /> {t.pratiche.deleteSelected}</button>
                        </>
                    )}
                    <button onClick={exportPdf} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-accent"><Download size={12} /> PDF</button>
                    <button onClick={exportExcel} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-accent"><Download size={12} /> Excel</button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full table-fixed text-sm [&_th]:border-r-2 [&_th]:border-white [&_td]:border-r [&_td]:border-orange-200 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0">
                    <thead className="bg-primary text-xs uppercase tracking-wide text-primary-foreground border-b-2 border-white">
                        <tr>
                            {mode === "to_send" && (
                                <th className="px-3 py-3 text-center font-bold uppercase w-10">
                                    <input type="checkbox" checked={allVisibleSelected} onChange={(e) => toggleAllVisible(e.target.checked)} aria-label="Seleziona tutte" />
                                </th>
                            )}
                            {columnOrder.map((key) => {
                                const col = columns[key];
                                if (!col) return null;
                                return (
                                    <th
                                        key={key}
                                        draggable
                                        onDragStart={() => setDraggedColumn(key)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => { moveColumn(draggedColumn, key); setDraggedColumn(null); }}
                                        onDragEnd={() => setDraggedColumn(null)}
                                        onClick={() => toggleSort(key)}
                                        title="Clicca per ordinare; trascina il titolo per spostare la colonna; trascina il bordo destro per ridimensionarla"
                                        style={columnWidths[key] ? { width: `${columnWidths[key]}px` } : undefined}
                                        className={`${col.headerClass} relative cursor-move select-none group ${draggedColumn === key ? "opacity-50" : ""}`}
                                    >
                                        <span className="inline-flex items-center gap-1 truncate">{col.label}{sortConfig.key === key ? (sortConfig.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                                        <span
                                            role="separator"
                                            aria-label={`Ridimensiona colonna ${col.label}`}
                                            onMouseDown={(e) => resizeColumn(key, e)}
                                            onClick={(e) => e.stopPropagation()}
                                            onDragStart={(e) => e.preventDefault()}
                                            className="absolute right-0 top-0 z-10 h-full w-3 cursor-col-resize border-r-2 border-white bg-white/0 hover:bg-white/50 group-hover:bg-white/20"
                                        />
                                    </th>
                                );
                            })}
                            {mode === "to_send" && <th className="px-2 py-3 text-center font-bold uppercase">Azioni</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {filteredRows.map((r) => (
                            <tr key={r.p.id} data-testid={`pratica-table-row-${r.p.id}`} onClick={() => navigate(`/pratiche/${r.p.id}`)} className="hover:bg-accent/30 cursor-pointer transition-colors">
                                {mode === "to_send" && (
                                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={Boolean(selected[r.p.id])} onChange={(e) => setSelected((cur) => ({ ...cur, [r.p.id]: e.target.checked }))} aria-label={`Seleziona ${r.subject}`} />
                                    </td>
                                )}
                                {columnOrder.map((key) => {
                                    const col = columns[key];
                                    if (!col) return null;
                                    return <td key={key} style={columnWidths[key] ? { width: `${columnWidths[key]}px` } : undefined} className={`${col.cellClass} overflow-hidden`}>{col.render(r)}</td>;
                                })}
                                {mode === "to_send" && (
                                    <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex justify-center gap-1">
                                            <button data-testid={`send-pratica-${r.p.id}`} onClick={async () => { try { await prepareAndSend(r.p); toast.success(t.telematico.sendStarted); await reload?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); } }} className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-primary text-white hover:bg-orange-600" title="Invia all'AdE"><Send size={14} /></button>
                                            <button data-testid={`delete-pratica-${r.p.id}`} onClick={() => deleteOne(r.p.id)} className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100" title={t.pratiche.delete}><Trash2 size={14} /></button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!filteredRows.length && <div className="p-8 text-center text-sm text-muted-foreground">Nessun risultato con i filtri selezionati.</div>}
            </div>
        </div>
    );
}

function invioOutcome(inv) {
    if (!inv) return "";
    if (inv.status === "failed" && !inv.protocol) return "FALLITO";
    if (inv.status === "completed" || inv.protocol || inv.esito === "ACCOLTA" || inv.esito === "SCARTATA") return "RIUSCITO";
    return "RIUSCITO";
}

function InvioDetailsPanel({ inv, t, onDownloadRicevuta, onFetchRicevuta }) {
    return (
        <div className="rounded-lg border border-orange-300 bg-orange-50 p-4 text-xs">
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <span data-testid={`invio-status-${inv.id}`} className={`rounded border border-orange-300 bg-white px-2 py-1 font-bold uppercase text-orange-800`}>
                    {t.telematico.status[inv.status] || inv.status}
                </span>
                {inv.esito && (
                    <span data-testid={`invio-esito-${inv.id}`} className="rounded border border-orange-300 bg-white px-2 py-1 font-bold uppercase text-orange-800">
                        {t.telematico.esito[inv.esito] || inv.esito}
                    </span>
                )}
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-1 rounded-md border border-orange-200 bg-white px-3 py-2 sm:grid-cols-2">
                <InvioDetailRow label="Adempimento" value={inv.tipo_adempimento || "—"} />
                <InvioDetailRow label="Ambiente" value={inv.environment || inv.mode_label || inv.mode || "—"} />
                <InvioDetailRow label="Codice fiscale" value={inv.codice_fiscale || "—"} mono />
                <InvioDetailRow label="Partita IVA" value={inv.partita_iva || "—"} mono />
                <InvioDetailRow label="File" value={inv.ccf_file_name || "—"} />
                <InvioDetailRow label="SHA-256 file" value={inv.ccf_sha256 || "—"} mono />
                <InvioDetailRow label="Protocollo" value={inv.protocol || "—"} mono />
                <InvioDetailRow label="Data invio" value={inv.created_at ? new Date(inv.created_at).toLocaleString("it-IT") : "—"} />
            </div>
            {(inv.ricevuta_path || inv.ricevuta_rel_path) && (
                <div data-testid={`ricevuta-official-${inv.id}`} className="mt-3 rounded-md border border-orange-300 bg-white px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <div className="text-xs font-bold uppercase text-orange-900">{t.telematico.ricevutaOfficial}</div>
                            <div className="text-[11px] text-black">{t.telematico.ricevutaAvailableHint}</div>
                        </div>
                        <button data-testid={`download-ricevuta-pdf-${inv.id}`} onClick={() => onDownloadRicevuta(inv.id)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold uppercase text-white hover:bg-orange-600">
                            <FileDown size={12} /> {t.telematico.downloadRicevuta}
                        </button>
                    </div>
                </div>
            )}
            {inv.status === "completed" && inv.mode !== "simulazione" && !inv.esito && (
                <button data-testid={`fetch-ricevuta-${inv.id}`} onClick={() => onFetchRicevuta(inv.id)} className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-orange-300 bg-white px-3 py-1.5 text-xs font-bold uppercase text-orange-800 hover:bg-orange-100">
                    <Download size={12} /> {t.telematico.fetchRicevuta}
                </button>
            )}
            {inv.error && (
                <details className="mt-3 rounded-md border border-orange-300 bg-white px-3 py-2 text-orange-900">
                    <summary className="cursor-pointer font-bold uppercase">Errore tecnico</summary>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px]">{typeof inv.error === "string" ? inv.error : JSON.stringify(inv.error, null, 2)}</pre>
                </details>
            )}
        </div>
    );
}

function InvioDetailRow({ label, value, mono = false }) {
    return (
        <div className="flex min-w-0 gap-2">
            <span className="shrink-0 font-bold uppercase text-orange-800">{label}:</span>
            <span className={`min-w-0 truncate text-black ${mono ? "font-mono" : ""}`}>{value}</span>
        </div>
    );
}

export function PraticaDetail() {
    const { id } = useParams();
    const { t } = useI18n();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [pratica, setPratica] = useState(null);
    const [errors, setErrors] = useState([]);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [titleDraft, setTitleDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [remoteSchema, setRemoteSchema] = useState(null);
    const [spidOpen, setSpidOpen] = useState(false);
    const [calcSummary, setCalcSummary] = useState(null);
    const [hasInvio, setHasInvio] = useState(false);
    const [detailInvio, setDetailInvio] = useState(null);
    const [invioDetailsOpen, setInvioDetailsOpen] = useState(false);
    const fileInputRef = React.useRef(null);
    const attachmentInputRef = React.useRef(null);
    const [attachmentDocCode, setAttachmentDocCode] = useState("DIS");
    const [attachmentRecipient, setAttachmentRecipient] = useState("RI");
    const [attachmentSigned, setAttachmentSigned] = useState(true);
    const [comunicaPreflight, setComunicaPreflight] = useState(null);

    const load = useCallback(async () => {
        const [r, inviiRes, preflightRes] = await Promise.all([
            api.get(`/pratiche/${id}`),
            api.get(`/telematico/invii`).catch(() => ({ data: [] })),
            api.get(`/pratiche/${id}/comunica-preflight`).catch(() => ({ data: null })),
        ]);
        setPratica(r.data);
        const matchingInvii = (inviiRes.data || []).filter((invio) => invio.pratica_id === id);
        const latestInvio = matchingInvii.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0] || null;
        setDetailInvio(latestInvio);
        setHasInvio(Boolean(latestInvio));
        setForm(r.data.form_data || {});
        setTitleDraft(r.data.title || "");
        setErrors(r.data.validation_errors || []);
        setComunicaPreflight(preflightRes.data);
        if (r.data.module_code === "COMUNICA_RI" && r.data.form_data?.practice_type) {
            try {
                const cr = await api.get(`/comunica/catalog/${r.data.form_data.practice_type}`);
                setRemoteSchema(catalogToFormSchema(cr.data));
            } catch {
                setRemoteSchema(null);
            }
        }
        if (!FORM_SCHEMAS[r.data.module_code] && r.data.module_code !== "COMUNICA_RI") {
            try {
                const rs = await api.get(`/pratiche/modules/${r.data.module_code}/schema`);
                setRemoteSchema(rs.data);
            } catch {}
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    if (!pratica) return <div className="p-10 text-muted-foreground text-sm">{t.common.loading}</div>;

    const schema = remoteSchema || FORM_SCHEMAS[pratica.module_code];
    const isDichiarativo = false;
    const alreadySent = hasInvio || ["submitted", "accepted", "rejected"].includes(pratica.status);

    const runInfoCamereCommand = async (command) => {
        if (!pratica.task_id) return toast.error("Attività ComUnica non collegata alla pratica");
        let commandPayload = {};
        if (command === "infocamere_check") {
            const accepted = window.confirm(
                "Confermi di aver preso atto che la Camera può rettificare gli importi durante l’istruttoria? " +
                "Nel controllo mock gli importi sono impostati a zero e non avviene alcun addebito."
            );
            if (!accepted) return;
            commandPayload = {
                presentazione: {
                    importi: { diritti: "0.00" },
                    permetti_rettifica: true,
                    email_dichiarante: form?.dichiarante?.email || undefined,
                },
            };
        }
        setBusy(true);
        try {
            await api.post(`/v1/comunica/tasks/${pratica.task_id}/commands`, {
                command, pratica_id: id,
                payload: commandPayload,
                idempotency_key: `${command}:${id}:${Date.now()}`,
            });
            toast.success(command === "infocamere_check" ? "Controllo mock accodato" : "Aggiornamento esito accodato");
            setTimeout(load, 1400);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Operazione non riuscita");
        } finally { setBusy(false); }
    };

    const runCalculations = async () => {
        setBusy(true);
        try {
            if (editing) await api.put(`/pratiche/${id}`, { title: titleDraft, form_data: form });
            const r = await api.post(`/pratiche/${id}/calculate`);
            setCalcSummary(r.data.summary || []);
            setForm(r.data.form_data || {});
            setPratica((p) => ({ ...p, form_data: r.data.form_data }));
            toast.success(t.calc.done);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore calcolo");
        } finally { setBusy(false); }
    };

    const richiestaPrecompilata = async () => {
        setBusy(true);
        try {
            await api.post(`/precompilata/richiesta`, { pratica_id: id });
            toast.success(t.precompilata.richiestaStarted);
            navigate("/telematico");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore richiesta");
        } finally { setBusy(false); }
    };

    const uploadPrecompilata = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const tid = toast.loading(t.precompilata.importing);
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/precompilata/import/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
            toast.success(`${t.precompilata.imported} (${(r.data.quadri_importati || []).join(", ") || "frontespizio"})`, { id: tid });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore import", { id: tid });
        } finally { setBusy(false); }
    };

    const updField = (path, value) => {
        const next = JSON.parse(JSON.stringify(form));
        const parts = path.split(".");
        let cur = next;
        for (let i = 0; i < parts.length - 1; i++) {
            if (!cur[parts[i]]) cur[parts[i]] = {};
            cur = cur[parts[i]];
        }
        cur[parts[parts.length - 1]] = value;
        setForm(next);
    };

    const saveDraft = async () => {
        setBusy(true);
        try {
            await api.put(`/pratiche/${id}`, { title: titleDraft, form_data: form });
            const validation = await api.post(`/pratiche/${id}/validate`);
            await load();
            setEditing(false);
            if (validation.data.valid) {
                toast.success("Dati salvati e validati. Ora puoi generare la bozza JSON.");
            } else {
                toast.warning(`Dati salvati. Restano ${validation.data.errors?.length || 0} campi da completare.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore durante il salvataggio");
        } finally { setBusy(false); }
    };

    const uploadComunicaAttachment = async (event) => {
        const selected = event.target.files?.[0];
        event.target.value = "";
        if (!selected) return;
        const fd = new FormData();
        fd.append("file", selected);
        fd.append("tipo_documento", attachmentDocCode);
        fd.append("ente_destinatario", attachmentRecipient);
        fd.append("firmato", String(attachmentSigned));
        setBusy(true);
        try {
            await api.post(`/pratiche/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Allegato caricato e verificato");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore caricamento allegato");
        } finally { setBusy(false); }
    };

    const deleteComunicaAttachment = async (attachmentId) => {
        setBusy(true);
        try {
            await api.delete(`/pratiche/${id}/attachments/${attachmentId}`);
            toast.success("Allegato rimosso");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore rimozione allegato");
        } finally { setBusy(false); }
    };

    const validate = async () => {
        setBusy(true);
        try {
            await api.put(`/pratiche/${id}`, { title: titleDraft, form_data: form });
            const r = await api.post(`/pratiche/${id}/validate`);
            setErrors(r.data.errors || []);
            await load();
            if (r.data.valid) toast.success("Validazione completata: la pratica è pronta per generare la bozza.");
            else toast.warning(`Validazione completata: restano ${r.data.errors?.length || 0} campi da completare.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore durante la validazione");
        } finally { setBusy(false); }
    };

    const generate = async () => {
        setBusy(true);
        try {
            await api.put(`/pratiche/${id}`, { title: titleDraft, form_data: form });
            await api.post(`/pratiche/${id}/generate`);
            await load();
            toast.success(pratica.module_code === "COMUNICA_RI" ? "Bozza strutturata del laboratorio generata. Nessun invio esterno." : t.pratiche.savedReady);
        } catch (e) {
            const det = e?.response?.data?.detail;
            if (det?.errors) {
                setErrors(det.errors);
                toast.error("Validazione fallita. Controlla gli errori.");
            } else {
                toast.error(typeof det === "string" ? det : (e?.message || "Errore"));
            }
        } finally { setBusy(false); }
    };

    const download = async (format = "ccf") => {
        const downloadId = toast.loading(format === "pdf" ? "Generazione PDF in corso..." : format === "json" ? "Download bozza JSON in corso..." : "Download .ccf in corso...");
        try {
            const r = await api.post(`/pratiche/${id}/download-token?format=${format}`);
            const relUrl = r?.data?.url;
            const filename = r?.data?.filename;
            if (!relUrl) throw new Error("URL di download non ricevuto");
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${relUrl}`;
            const a = document.createElement("a");
            a.href = fullUrl;
            if (filename) a.download = filename;
            a.rel = "noopener";
            a.target = "_self";
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { if (a.parentNode) a.parentNode.removeChild(a); }, 1500);
            toast.success(format === "pdf" ? "PDF inviato al browser" : format === "json" ? "Bozza JSON inviata al browser" : "File tecnico inviato al browser", {
                id: downloadId,
                description: filename ? `Salvato come ${filename} nella cartella Download` : undefined,
            });
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Errore sconosciuto";
            toast.error(`Errore download: ${msg}`, { id: downloadId });
        }
    };

    const downloadInvioRicevuta = async (id, fmt = "pdf") => {
        const tid = toast.loading("Download ricevuta...");
        try {
            const r = await api.post(`/telematico/invii/${id}/ricevuta-token?fmt=${fmt}`);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const a = document.createElement("a");
            a.href = `${backendUrl}${r.data.url}`;
            a.download = r.data.filename;
            a.rel = "noopener";
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            setTimeout(() => a.remove(), 1500);
            toast.success(t.telematico.ricevutaDownloaded, { id: tid });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore download", { id: tid });
        }
    };

    const fetchInvioRicevuta = async (id) => {
        try {
            await api.post(`/telematico/invii/${id}/ricevuta/fetch`);
            toast.success(t.telematico.ricevutaFetched);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore recupero ricevuta");
        }
    };

    const submitTelematico = async () => {
        setBusy(true);
        try {
            if (editing) await api.put(`/pratiche/${id}`, { title: titleDraft, form_data: form });
            if (!pratica.file_generated) await api.post(`/pratiche/${id}/generate`);
            await api.post(`/telematico/invii`, { pratica_id: id });
            toast.success(t.telematico.sendStarted);
            navigate("/telematico");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Errore invio");
        } finally { setBusy(false); }
    };

    const duplicate = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/pratiche/${id}/duplicate`);
            toast.success(t.templates.duplicated);
            navigate(`/pratiche/${r.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };

    const saveAsTemplate = async () => {
        const name = window.prompt(t.templates.namePrompt, pratica.title);
        if (!name) return;
        let isGlobal = false;
        if (user?.role === "super_admin") {
            isGlobal = window.confirm(t.templates.globalPrompt);
        }
        setBusy(true);
        try {
            await api.post(`/templates`, { pratica_id: id, name, is_global: isGlobal });
            toast.success(t.templates.saved);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen p-6 lg:p-10 max-w-5xl mx-auto bg-white">
            <button onClick={() => navigate("/pratiche")} className="text-sm text-orange-700 hover:text-black mb-4 flex items-center gap-1">
                ← Pratiche
            </button>

            <header className="mb-6 flex items-start justify-between gap-4 flex-wrap rounded-lg border border-orange-300 bg-white px-4 py-3 shadow-sm">
                <div>
                    <div className="text-xs uppercase tracking-[0.1em] font-bold uppercase text-orange-700 mb-1">{pratica.module_code}</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight uppercase text-black">{pratica.title}</h1>
                </div>
                <span className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-wider border shadow-sm ${STATUS_COLORS[pratica.status] || ""}`}>
                    {t.pratiche.status[pratica.status] || pratica.status}
                </span>
            </header>

            {!alreadySent && (
                <div className="mb-4 rounded-lg border border-orange-300 bg-white px-4 py-3 shadow-sm">
                    <label className="block text-xs uppercase tracking-[0.1em] font-bold uppercase text-orange-900 mb-1">Nome file/pratica</label>
                    <input
                        data-testid="pratica-title-input"
                        value={titleDraft}
                        onChange={(e) => setTitleDraft(e.target.value)}
                        onBlur={async () => {
                            const nextTitle = titleDraft.trim();
                            if (!nextTitle || nextTitle === pratica.title) return;
                            try {
                                const r = await api.put(`/pratiche/${id}`, { title: nextTitle });
                                setPratica(r.data);
                                toast.success("Nome pratica salvato");
                            } catch (e) {
                                toast.error(e?.response?.data?.detail || "Errore salvataggio nome");
                            }
                        }}
                        className="w-full rounded-md border border-orange-300 bg-white px-3 py-2 text-sm font-bold uppercase text-black focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                        placeholder="Nome file/pratica"
                    />
                    <div className="mt-1 text-[11px] text-black/70">Questo nome sarà visibile nelle liste e usato come nome del file scaricato.</div>
                </div>
            )}

            {errors.length > 0 && (
                <div data-testid="validation-errors" className="mb-4 rounded-md border border-orange-300 bg-white px-4 py-3">
                    <div className="flex items-center gap-2 mb-2 font-bold uppercase text-orange-900">
                        <AlertCircle size={14} /> {errors.length} {errors.length === 1 ? "errore" : "errori"} di validazione
                    </div>
                    <ul className="text-xs text-orange-900 space-y-0.5 list-disc list-inside">
                        {errors.map((e, i) => (
                            <li key={i}>{typeof e === "string" ? e : <><span className="font-mono">{e.field}</span>: {e.message}</>}</li>
                        ))}
                    </ul>
                </div>
            )}

            {pratica.module_code === "COMUNICA_RI" && comunicaPreflight && (
                <ComunicaPreflightCard
                    data={comunicaPreflight}
                    onRefresh={load}
                    onTelemaco={() => navigate("/account/telemaco")}
                    onCheck={() => runInfoCamereCommand("infocamere_check")}
                    onPoll={() => runInfoCamereCommand("infocamere_poll")}
                    busy={busy}
                />
            )}

            {pratica.file_generated && !alreadySent && (
                <div data-testid="file-ready-banner" className="mb-4 rounded-md border border-primary bg-primary px-4 py-3 shadow-sm">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-white text-sm font-bold uppercase">
                            <CheckCircle2 size={16} /> {pratica.module_code === "COMUNICA_RI" ? "Bozza Lab generata — nessun invio esterno." : "Pratica salvata e pronta per l'invio all'Agenzia delle Entrate."}
                        </div>
                        <div className="flex gap-2">
                            <button data-testid="download-pdf" onClick={() => download(pratica.module_code === "COMUNICA_RI" ? "zip" : "pdf")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white bg-white text-orange-700 text-xs font-bold uppercase hover:bg-orange-50 transition-colors">
                                <FileDown size={12} /> {pratica.module_code === "COMUNICA_RI" ? "Scarica pacchetto Lab ZIP" : "Scarica PDF"}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {isDichiarativo && (
                <div data-testid="precompilata-card" className="mb-6 rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
                    <div className="text-sm font-bold uppercase text-orange-950 mb-1">{t.precompilata.title}</div>
                    <p className="text-xs text-orange-900/80 mb-3">{t.precompilata.subtitle}</p>
                    <div className="flex flex-wrap gap-2">
                        <button data-testid="precompilata-delega" onClick={richiestaPrecompilata} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-white hover:bg-orange-600 text-xs font-bold uppercase transition-colors disabled:opacity-50">
                            <Send size={12} /> {t.precompilata.viaDelega}
                        </button>
                        <button data-testid="precompilata-spid" onClick={() => setSpidOpen(!spidOpen)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-orange-300 bg-white hover:bg-orange-50 text-orange-900 text-xs font-bold uppercase transition-colors">
                            <FileDown size={12} /> {t.precompilata.viaSpid}
                        </button>
                        <input ref={fileInputRef} type="file" accept=".xml,.pdf" className="hidden" onChange={uploadPrecompilata} data-testid="precompilata-file-input" />
                    </div>
                    {spidOpen && (
                        <div data-testid="spid-instructions" className="mt-3 rounded-md border border-orange-200 bg-white px-4 py-3">
                            <div className="text-xs font-bold uppercase mb-2">{t.precompilata.spidTitle}</div>
                            <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside mb-3">
                                {t.precompilata.spidSteps.map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                            <button data-testid="precompilata-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold uppercase transition-colors disabled:opacity-50">
                                {t.precompilata.uploadBtn}
                            </button>
                        </div>
                    )}
                    {pratica.precompilata_path && (
                        <div className="mt-2 text-xs text-orange-900 font-bold uppercase">✓ {t.precompilata.received}</div>
                    )}
                </div>
            )}

            {/* Action buttons */}
            <div className="mb-3 rounded-lg border border-orange-300 bg-orange-50 px-4 py-3">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-900">Passaggi della pratica</div>
                <div className="mt-1 text-xs text-orange-900/80">1. Modifica i dati → 2. Salva e valida → 3. Genera la bozza → 4. Scarica il JSON.</div>
            </div>
            <div className="mb-6 flex flex-wrap gap-2">
                {!editing && (
                    <button data-testid="edit-pratica" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-orange-300 bg-white text-black hover:bg-orange-50 text-sm transition-colors">
                        <Edit2 size={12} /> Modifica dati
                    </button>
                )}
                {!editing && (
                    <button data-testid="validate-pratica" onClick={validate} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-primary bg-white text-orange-800 hover:bg-orange-50 text-sm font-bold uppercase transition-colors disabled:opacity-50">
                        <CheckCircle2 size={12} /> Valida dati
                    </button>
                )}
                {CALC_MODULES.includes(pratica.module_code) && (
                    <button data-testid="calculate-pratica" onClick={runCalculations} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-white hover:bg-orange-600 text-sm font-bold uppercase transition-colors disabled:opacity-50">
                        <Calculator size={12} /> {t.calc.button}
                    </button>
                )}
                {!editing && (
                    <button data-testid="generate-pratica" onClick={generate} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-bold uppercase transition-colors disabled:opacity-50">
                        <FileText size={12} /> {pratica.module_code === "COMUNICA_RI" ? "Genera pacchetto Lab" : t.pratiche.generate}
                    </button>
                )}
                <button data-testid="duplicate-pratica" onClick={duplicate} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-orange-300 bg-white text-black hover:bg-orange-50 text-sm transition-colors disabled:opacity-50">
                    <Copy size={12} /> {t.templates.duplicate}
                </button>
                <button data-testid="save-as-template" onClick={saveAsTemplate} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-orange-300 bg-white text-black hover:bg-orange-50 text-sm transition-colors disabled:opacity-50">
                    <BookmarkPlus size={12} /> {t.templates.saveAs}
                </button>
                {!alreadySent && pratica.module_code !== "COMUNICA_RI" && (
                    <button data-testid="submit-telematico" onClick={submitTelematico} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-white hover:bg-orange-600 text-sm font-bold uppercase shadow-sm transition-colors disabled:opacity-50">
                        <Send size={12} /> {t.telematico.sendToAde}
                    </button>
                )}
            </div>

            {calcSummary && calcSummary.length > 0 && (
                <div data-testid="calc-summary" className="mb-6 rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-bold uppercase text-orange-950 mb-2">
                        <Calculator size={14} /> {t.calc.summaryTitle}
                    </div>
                    <ul className="text-xs text-orange-950/90 space-y-1">
                        {calcSummary.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                    <div className="text-[10px] text-orange-900/60 mt-2">{t.calc.disclaimer}</div>
                </div>
            )}

            {/* Schema-driven form */}
            {pratica.module_code === "COMUNICA_RI" && (
                <div className="mb-5 rounded-lg border border-orange-300 bg-white p-4 shadow-sm">
                    <div className="mb-3 text-sm font-bold uppercase text-orange-900">Allegati tecnici</div>
                    <div className="grid gap-2 sm:grid-cols-4">
                        <input value={attachmentDocCode} onChange={(e) => setAttachmentDocCode(e.target.value.toUpperCase())} maxLength={3} placeholder="Codice DOC" className="rounded-md border border-orange-300 px-3 py-2 text-sm" />
                        <select value={attachmentRecipient} onChange={(e) => setAttachmentRecipient(e.target.value)} className="rounded-md border border-orange-300 px-3 py-2 text-sm"><option>RI</option><option>AE</option><option>IP</option><option>IL</option><option>AA</option><option>ML</option></select>
                        <label className="flex items-center gap-2 rounded-md border border-orange-300 px-3 py-2 text-sm"><input type="checkbox" checked={attachmentSigned} onChange={(e) => setAttachmentSigned(e.target.checked)} /> Firmato .p7m</label>
                        <button onClick={() => attachmentInputRef.current?.click()} disabled={busy || !attachmentDocCode} className="rounded-md bg-primary px-3 py-2 text-sm font-bold uppercase text-white disabled:opacity-50">Carica file</button>
                        <input ref={attachmentInputRef} type="file" accept=".pdf,.p7m,.xml,.xbrl,.html,.xhtml" className="hidden" onChange={uploadComunicaAttachment} />
                    </div>
                    <div className="mt-3 space-y-2">
                        {(form.allegati || []).map((a, index) => (
                            <div key={a.id || index} className="flex items-center justify-between rounded border border-orange-200 px-3 py-2 text-xs">
                                <span><strong>{a.nome_file}</strong> · {a.tipo_documento} · {a.ente_destinatario} · {a.size_bytes ? `${a.size_bytes} byte` : "solo metadati"}</span>
                                {a.id && <button onClick={() => deleteComunicaAttachment(a.id)} className="text-orange-700 hover:text-black"><Trash2 size={14} /></button>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="rounded-lg border border-orange-200 bg-card overflow-hidden shadow-sm">
                {schema ? (
                    <ModuleForm schema={schema} form={form} editing={editing} updField={updField} />
                ) : (
                    <div className="p-8 text-sm text-muted-foreground">Modulo non ancora supportato nell'editor.</div>
                )}

                {editing && (
                    <div className="px-5 py-4 bg-orange-50 border-t border-orange-200 flex justify-end gap-2">
                        <button onClick={() => { setEditing(false); setForm(pratica.form_data || {}); }} className="px-4 py-2 rounded-md bg-white border border-orange-300 text-black hover:bg-orange-50 text-sm transition-colors">Annulla</button>
                        <button data-testid="save-pratica-form" onClick={saveDraft} disabled={busy} className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-bold uppercase transition-colors disabled:opacity-50">Salva e valida</button>
                    </div>
                )}
            </div>

            {pratica.submitted_protocol && (
                <div className="mt-4 text-sm">
                    <span className="text-muted-foreground">Protocollo invio:</span> <span className="font-mono font-bold uppercase">{pratica.submitted_protocol}</span>
                </div>
            )}

            {detailInvio && (
                <div className="mt-6 rounded-lg border border-orange-300 bg-white shadow-sm">
                    <button
                        type="button"
                        data-testid="toggle-invio-details-detail"
                        onClick={() => setInvioDetailsOpen((v) => !v)}
                        className="flex w-full items-center justify-between gap-3 bg-primary px-4 py-3 text-left text-sm font-bold uppercase text-white hover:bg-orange-600"
                    >
                        <span>Dettagli invio telematico</span>
                        <span className="ms-auto rounded border border-white bg-white px-2 py-0.5 text-[11px] font-bold uppercase text-orange-800">{invioOutcome(detailInvio)}</span>
                        {invioDetailsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {invioDetailsOpen && (
                        <div className="p-4">
                            <InvioDetailsPanel inv={detailInvio} t={t} onDownloadRicevuta={downloadInvioRicevuta} onFetchRicevuta={fetchInvioRicevuta} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
